<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useFetch } from '@vueuse/core';
import EstimateBorder from '~/components/svg/EstimateBorder.vue';
import EnergySageLogo from '~/components/svg/EnergySageLogo.vue';
import EstimateResults from '~/components/widgets/SolarEstimate/EstimateResults.vue';
import EsButton from '@energysage/es-ds-components/components/es-button.vue';
import EsToggle from '@energysage/es-ds-components/components/es-toggle.vue';
import EsSlider from '@energysage/es-ds-components/components/es-slider.vue';
import SimpleTooltip from '~/components/shared/SimpleTooltip.vue';
import IconInfo from '@energysage/es-ds-components/components/icon/info.vue';
import * as Sentry from '@sentry/vue';
import AutocompleteWidget from '~/components/shared/AutocompleteWidget.vue';

const latitude = ref(0);
const longitude = ref(0);
const zipCode = ref('');
const state = ref('');
const address = ref('');
const city = ref('');
const batteryToggle = ref(false);
const federalTaxCreditToggle = ref(false);
const monthlyBill = ref(200); // Default bill to national average

const estimateResponse = ref<any>(null);
const validationState = ref(false);
const inputsChanged = ref(false);
const status = ref('idle'); // 'idle' | 'pending' | 'success' | 'error'
const props = defineProps<{
    rc?: string;
}>();

// Function to fetch compass estimate
const fetchCompassEstimate = async () => {
    // @ts-ignore
    const vueWidgetBffSubdomain = import.meta.env.VITE_VUE_WIDGETS_BFF_DOMAIN;
    if (!vueWidgetBffSubdomain) {
        Sentry.captureMessage('VITE_VUE_WIDGETS_BFF_DOMAIN not configured', 'error');
        return;
    }

    if (!inputsChanged.value) {
        return; // do not do multiple calls on same info
    }
    if (
        !zipCode.value ||
        !state.value ||
        !latitude.value ||
        !longitude.value ||
        !monthlyBill.value ||
        !address.value ||
        !city.value
    ) {
        Sentry.captureMessage('Missing required fields for compass estimate', 'warning');
        return;
    }

    const queryParams = new URLSearchParams({
        zip_code: zipCode.value,
        state: state.value,
        latitude: latitude.value.toString(),
        longitude: longitude.value.toString(),
        electric_bill: monthlyBill.value.toString(),
        address: address.value,
        city: city.value,
    });

    const apiUrl = `https://${vueWidgetBffSubdomain}/v1/compass-estimate?${queryParams}`;

    try {
        status.value = 'pending';
        const { data, error, execute } = useFetch(apiUrl, { immediate: false }).json();

        await execute();

        if (error.value) {
            status.value = 'error';
            Sentry.captureMessage(`Failed to fetch compass estimate: ${error.value}`, 'error');
            return;
        }

        if (data.value) {
            estimateResponse.value = data.value;
            inputsChanged.value = false; // only change during success to prevent locking the widget in the event of an error
            status.value = 'success';
        }
    } catch (err) {
        status.value = 'error';
        Sentry.captureMessage(`Failed to fetch compass estimate: ${err}`, 'error');
    }
};

const totalCost = computed(() => {
    if (estimateResponse.value) {
        const solarType = batteryToggle.value ? 'solar_with_storage' : 'solar_only';
        const incentiveType = federalTaxCreditToggle.value ? 'incentives' : 'no_incentives';

        return estimateResponse.value[solarType]?.cash?.[incentiveType]?.net_cost_dollars || null;
    }
    return 0;
});

const yearSavings = computed(() => {
    if (estimateResponse.value) {
        const solarType = batteryToggle.value ? 'solar_with_storage' : 'solar_only';
        const incentiveType = federalTaxCreditToggle.value ? 'incentives' : 'no_incentives';

        return estimateResponse.value[solarType]?.cash?.[incentiveType]?.twenty_five_year_savings_dollars || null;
    }
    return 0;
});

const monthlySavings = computed(() => {
    if (estimateResponse.value) {
        const solarType = batteryToggle.value ? 'solar_with_storage' : 'solar_only';
        const incentiveType = federalTaxCreditToggle.value ? 'incentives' : 'no_incentives';

        return estimateResponse.value[solarType]?.cash?.[incentiveType]?.monthly_savings_dollars || null;
    }
    return 0;
});

const updateAddress = (selectedAddress: any) => {
    latitude.value = selectedAddress.latitude;
    longitude.value = selectedAddress.longitude;
    zipCode.value = selectedAddress.postalCode;
    state.value = selectedAddress.state;
    address.value = `${selectedAddress.streetNumber} ${selectedAddress.streetName}`;
    city.value = selectedAddress.city;
    inputsChanged.value = true;
};

const updateValidation = (newValidationState: boolean) => {
    validationState.value = newValidationState;
};

watch(monthlyBill, () => {
    inputsChanged.value = true;
});
</script>

<template>
    <div class="solar-estimate-container bg-white">
        <EstimateBorder
            data-testid="estimate-border"
            class="top-0" />
        <div class="d-flex flex-nowrap justify-content-center">
            <div class="widget-content px-100 py-100 w-100">
                <EnergySageLogo
                    data-testid="energy-sage-logo"
                    class="my-50" />
                <h1 class="font-size-400 text-blue-900 pt-25">See if solar is right for you</h1>
                <h4 class="font-size-100 m-0 pb-50">Get an estimate on your savings and cost</h4>
                <form @submit.prevent="fetchCompassEstimate">
                    <AutocompleteWidget
                        class="pb-100"
                        :rc="props.rc"
                        @update-address="updateAddress"
                        @update-validation="updateValidation" />
                    <h4
                        id="monthly-bill-label"
                        class="font-size-100 mb-0">
                        How much is your average electric bill?
                    </h4>
                    <EsSlider
                        v-model="monthlyBill"
                        aria-labelledby="monthly-bill-label"
                        :label-formatter="(val: Number) => `$${val}`"
                        :tooltip-formatter="(val: Number) => `$${val}`"
                        :disabled="status === 'pending'"
                        :min="50"
                        :max="1200"
                        :step="10"
                        class="" />
                    <div class="toggle-section mb-25">
                        <div class="d-flex align-items-center mb-50">
                            <h4 class="font-size-100 mb-0">Do you want to store excess energy?</h4>
                            <SimpleTooltip
                                trigger-class="ml-25"
                                position="left">
                                <template #trigger>
                                    <IconInfo
                                        width="24px"
                                        height="24px"
                                        class="text-blue-600" />
                                </template>
                                <h5 class="text-white mb-25">Why add a battery?</h5>
                                <p class="mb-0">
                                    You can use stored excess energy after the sun has gone down or during power
                                    outages.<br /><br />A battery may not make sense if your utility provider pays well
                                    for your excess energy.
                                </p>
                            </SimpleTooltip>
                        </div>
                        <EsToggle
                            id="battery-toggle"
                            v-model="batteryToggle"
                            class="pb-100"
                            :disabled="status === 'pending'">
                            <template #label> Add a battery (avg cost is $13k) </template>
                        </EsToggle>
                        <h4
                            v-if="validationState"
                            class="font-size-100 mb-50">
                            Will you apply for the federal tax credit?
                        </h4>
                        <EsToggle
                            v-if="validationState"
                            id="federal-tax-credit-toggle"
                            v-model="federalTaxCreditToggle"
                            class="pb-100"
                            :disabled="status === 'pending'">
                            <template #label> Save 30% with the federal tax credit </template>
                        </EsToggle>
                    </div>
                    <EstimateResults
                        v-if="status !== 'idle'"
                        :is-loading="status === 'pending'"
                        :total-cost="totalCost"
                        :year-savings="yearSavings"
                        :monthly-savings="monthlySavings" />
                    <EsButton
                        v-if="status !== 'idle'"
                        :href="`https://www.energysage.com/onboarding/start/${
                            props.rc ? `?zip_code=${zipCode}&rc=${props.rc}` : ''
                        }`"
                        target="_blank"
                        type="button"
                        class="widget-button rounded-sm font-size-75 mt-150 w-100"
                        >Get full estimate
                    </EsButton>
                    <EsButton
                        v-if="status === 'idle'"
                        type="submit"
                        class="widget-button rounded-sm font-size-75 w-100"
                        :disabled="!validationState"
                        >Next
                    </EsButton>
                    <EsButton
                        v-if="status !== 'idle'"
                        type="submit"
                        variant="link"
                        class="widget-button rounded-sm font-size-75 mt-50 w-100"
                        :disabled="!validationState || status === 'pending'"
                        >Recalculate
                    </EsButton>
                </form>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
@use '~/assets/scss/main.scss';
@use '@energysage/es-ds-styles/scss/variables' as variables;

.solar-estimate-container {
    max-width: 375px;
    @media (min-width: 768px) {
        min-width: 600px;
        max-width: 600px;
    }
}

.widget-content {
    text-align: left;
    max-width: 375px;
}

.widget-button {
    height: 36px;
}

.es-slider-labels {
    display: none !important;
}

.es-slider-root {
    margin-top: 45px !important;
    margin-bottom: 37px !important;
}

.p-slider {
    border-radius: 9px !important;
}

.es-slider-handle {
    width: 52px !important;
    height: 52px !important;
    bottom: -37px !important;
    box-shadow: none !important;
}

.es-slider-handle::before {
    bottom: -9px !important;
    right: -9px !important;
    /* important to prevent sliding from attempting to horizontally scroll on mobile */
    touch-action: none !important;
    cursor: pointer !important;

    @media not (prefers-reduced-motion) {
        transition: box-shadow 0.15s ease-in-out, left 0.05s ease-in-out !important;
    }
}
</style>
